'use client';

import Link from 'next/link';
import { useMemo, useState } from 'react';

import {
    Button,
    ButtonVariant,
    Chips,
    ChipsList,
    Container,
    DirectionEnum,
    FlexBox,
    FlexItem,
    IconName,
    Text,
    TextLevels,
    Title,
    TitleLevels,
} from '@bytel/trilogy-react';

import { PalpatineFilterModel } from '@app-types/api/palpatine';

type ProductFiltersProps = {
    hrefForSearch: (search: string) => string;
    palpatineModelFilters?: PalpatineFilterModel;
    currentSearch?: string;
    onSearch: (search: string) => void;
    onRemoveSearch: () => void;
};

const DEFAULT_FILTER_MODEL_NB_TO_SHOW = 10;

export function ProductFilters({
    hrefForSearch,
    palpatineModelFilters,
    currentSearch,
    onSearch,
    onRemoveSearch,
}: Readonly<ProductFiltersProps>) {
    const [shouldShowFilterModelFullList, setShouldShowFilterModelFullList] = useState(false);

    const visibleModels = useMemo(() => {
        if (!palpatineModelFilters?.link_seo?.length) return [];
        return palpatineModelFilters.link_seo.slice(
            0,
            shouldShowFilterModelFullList ? palpatineModelFilters.link_seo.length : DEFAULT_FILTER_MODEL_NB_TO_SHOW,
        );
    }, [palpatineModelFilters?.link_seo, shouldShowFilterModelFullList]);

    if (!palpatineModelFilters?.link_seo?.length) return null;

    return (
        <Container medium>
            <FlexBox gap={6} direction={DirectionEnum.COLUMN} className='has-text-centered is-fullwidth'>
                <FlexItem>
                    <Title level={TitleLevels.FIVE}>{palpatineModelFilters.title}</Title>
                    {palpatineModelFilters.subtitle ? (
                        <Text level={TextLevels.ONE}>{palpatineModelFilters.subtitle}</Text>
                    ) : null}
                </FlexItem>
                <FlexItem>
                    <ChipsList className='is-centered'>
                        {visibleModels?.map((model) => (
                            <Link
                                key={model.title_product}
                                href={hrefForSearch(model.title_product ?? '')}
                                onClick={(e) => {
                                    e.preventDefault();
                                }}
                            >
                                <Chips
                                    onClick={() => {
                                        if (currentSearch === model.title_product) {
                                            onRemoveSearch();
                                        } else if (model.title_product) {
                                            onSearch(model.title_product);
                                        }
                                    }}
                                    active={currentSearch ? model.title_product?.includes(currentSearch) : false}
                                >
                                    {model.title_product}
                                </Chips>
                            </Link>
                        ))}
                    </ChipsList>
                </FlexItem>
                <FlexItem>
                    <Button
                        variant={ButtonVariant.GHOST}
                        className='is-marginless w-fit'
                        iconName={!shouldShowFilterModelFullList ? IconName.ARROW_DOWN : IconName.ARROW_UP}
                        onClick={() => setShouldShowFilterModelFullList((prev) => !prev)}
                    >
                        {shouldShowFilterModelFullList ? 'Afficher moins' : 'Afficher la liste complète'}
                    </Button>
                </FlexItem>
            </FlexBox>
        </Container>
    );
}
